import { Slot } from "radix-ui";
import type { Label as LabelPrimitive } from "radix-ui";
import * as React from "react";
import type { ControllerProps, FieldPath, FieldValues } from "react-hook-form";
import {
  Controller,
  FormProvider,
  useFormContext,
  useFormState,
} from "react-hook-form";

import { cn } from "~/components/ui/cn";
import { AlertCircleIcon } from "~/components/ui/icons/alert-circle";
import { Label } from "~/components/ui/label";

const Form = FormProvider;

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
  disabled?: boolean;
};

const FormFieldContext = React.createContext<FormFieldContextValue>(
  {} as FormFieldContextValue,
);

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext.Provider
      value={{ disabled: props.disabled, name: props.name }}
    >
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
};

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const { getFieldState } = useFormContext();
  const formState = useFormState({ name: fieldContext.name });
  const fieldState = getFieldState(fieldContext.name, formState);

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!fieldContext) {
    throw new Error("useFormField should be used within <FormField>");
  }

  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    disabled: fieldContext.disabled,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  };
};

type FormItemContextValue = {
  id: string;
};

const FormItemContext = React.createContext<FormItemContextValue>(
  {} as FormItemContextValue,
);

const FormItem: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...props
}) => {
  const id = React.useId();

  return (
    <FormItemContext.Provider value={{ id }}>
      <div
        data-slot="form-item"
        className={cn("space-y-2", className)}
        {...props}
      />
    </FormItemContext.Provider>
  );
};
FormItem.displayName = "FormItem";

const FormLabel: React.FC<React.ComponentProps<typeof LabelPrimitive.Root>> = ({
  className,
  ...props
}) => {
  const { disabled, error, formItemId } = useFormField();

  return (
    <div
      className={cn("flex flex-row items-center", className)}
      hidden={props.hidden}
    >
      <Label
        className={cn(
          "text-xs font-semibold uppercase tracking-wider",
          !error && !disabled && "text-gray-700",
          error && "text-red-600",
          disabled && "text-gray-500",
        )}
        htmlFor={formItemId}
        {...props}
      />
      {error ? (
        <div className="ml-1">
          <AlertCircleIcon className="size-4 text-red-600" />
        </div>
      ) : null}
    </div>
  );
};
FormLabel.displayName = "FormLabel";

const FormLabelCheckbox: React.FC<
  React.ComponentProps<typeof LabelPrimitive.Root>
> = ({ className, ...props }) => {
  const { formItemId } = useFormField();

  return (
    <Label
      className={cn(
        "cursor-pointer text-sm text-gray-700 peer-disabled:cursor-not-allowed peer-disabled:text-gray-300 peer-[[data-state=checked]]:text-gray-900 peer-[[data-state=checked]]:peer-disabled:text-gray-300 has-[~_.peer[data-state=checked]:disabled]:cursor-not-allowed has-[~_.peer[data-state=checked]:disabled]:text-gray-300 has-[~_.peer[data-state=checked]]:text-gray-900",
        className,
      )}
      htmlFor={formItemId}
      {...props}
    />
  );
};
FormLabelCheckbox.displayName = "FormLabelCheckbox";

const FormControl: React.FC<React.ComponentProps<typeof Slot.Root>> = ({
  ...props
}) => {
  const { error, formItemId, formDescriptionId, formMessageId } =
    useFormField();

  return (
    <Slot.Root
      data-slot="form-control"
      id={formItemId}
      aria-describedby={
        !error
          ? `${formDescriptionId}`
          : `${formDescriptionId} ${formMessageId}`
      }
      aria-invalid={!!error}
      {...props}
    />
  );
};
FormControl.displayName = "FormControl";

const FormMessage: React.FC<
  React.HTMLAttributes<HTMLParagraphElement> & { match: string }
> = ({ className, children, match, ...props }) => {
  const { error, formMessageId } = useFormField();

  if (error?.type !== match) {
    return null;
  }

  if (!children) {
    return null;
  }

  return (
    <p
      id={formMessageId}
      className={cn("text-xs text-red-600", className)}
      {...props}
    >
      {children}
    </p>
  );
};
FormMessage.displayName = "FormMessage";

const FormDescription: React.FC<React.HTMLAttributes<HTMLParagraphElement>> = ({
  className,
  ...props
}) => {
  const { disabled, error, formDescriptionId } = useFormField();

  if (error) return null;

  return (
    <p
      id={formDescriptionId}
      className={cn(
        "text-xs",
        !disabled && "text-gray-700",
        disabled && "text-gray-500",
        className,
      )}
      {...props}
    />
  );
};
FormDescription.displayName = "FormDescription";

const FormCounter: React.FC<React.HTMLAttributes<HTMLParagraphElement>> = ({
  className,
  ...props
}) => {
  const { disabled, error } = useFormField();

  const maxLengthError = error?.type === "maxLength";

  return (
    <p
      className={cn(
        "text-xs",
        !disabled && !maxLengthError && "text-gray-700",
        disabled && "text-gray-500",
        maxLengthError && "text-red-600",
        className,
      )}
      {...props}
    />
  );
};
FormCounter.displayName = "FormCounter";

export {
  useFormField,
  Form,
  FormControl,
  FormCounter,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormLabelCheckbox,
  FormMessage,
};
